import {EventHandler} from '@nolimitcity/slot-game/bin/core/event/EventHandler';
import {GameEvent} from '@nolimitcity/slot-game/bin/core/event/GameEvent';
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {Reels} from '@nolimitcity/slot-game/bin/core/reel/reelarea/Reels';
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {SequentiallyBetLineInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetLineInitialWPController";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {BarbarianFuryGameConfig} from "../../BarbarianFuryGameConfig";
import {BarbarianFuryGameModuleConfig} from "../../BarbarianFuryGameModuleConfig";
import {BarbarianFurySoundConfig} from "../../BarbarianFurySoundConfig";
import {BarbarianFuryGameMode} from "../../gamemode/BarbarianFuryGameMode";
import {BarbarianFuryReelSymbol} from "../../symbols/BarbarianFuryReelSymbol";
import {BFNudgeStackedWPController} from "../nudgeStacked/BFNudgeStackedWPController";
import {BarbarianFuryInitialWinCountUp} from "./BarbarianFuryInitialWinCountUp";

/**
 * Created by Ning Jiang on 10/10/2018.
 */

export class BarbarianFuryInitialWPController extends SequentiallyBetLineInitialWPController {

    private _symbolWinStates:boolean[][];
    private _winCountUp:BarbarianFuryInitialWinCountUp;
    private _symbolsNum:number;
    private _reelsNum:number;
    private _nextMode:string;

    constructor(indexes:[number, number]) {
        super(indexes, {
            name : "InitialWin",
            hasWinCountUp : true,
            shouldPlayBetLine : true,
            skippable : true,
            betWinMode : BetWinMode.BET_LINE,
        });

        this._winCountUp = new BarbarianFuryInitialWinCountUp();
        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        StageManager.getLayer(BarbarianFuryGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];
        this._reelsNum = GameConfig.instance.REELS_NUM;
    }

    /**
     * override this function to play sounds.
     */
    protected startWinPresentation():void {
        const winSound:string | null = BarbarianFurySoundConfig.getNormalWinSoundName(this._gameData!.currentWinRatioConfig!);

        if(winSound) {
            if (!BarbarianFuryGameConfig.instance.WIN_BELOW_STAKE) {
                SlotGame.sound.playEffect(winSound);
            }
        } else {
            SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.BIGWIN_INITIAL_WIN);
        }
        super.startWinPresentation();

    }

    protected getNextSymbolAnimationTimeOffset(duration:number):number {
        return 0.2;
    }

    protected getNextBetWinAnimationTimeOffset(lastSymbolDelay:number):number {
        return 0; // 0 to show all betlines at once, inititally it 0.0.5
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            return;
        }

        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;

        const countUpTimeline:TimelineLite = new TimelineLite();
        countUpTimeline.add(this._winCountUp.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier));
        countUpTimeline.add(() => SlotGame.winFieldController.showWinField());
        countUpTimeline.add(TweenLite.to(() => {}, 0.1, {}));

        timeline.add(countUpTimeline, 0);
    }

    protected parseFeatureGameData(data:ParsedGameData):void {
        this._nextMode = data.nextMode;
        this._symbolWinStates = ArrayHelper.initArrayWithValues<boolean[]>(this._reelsNum, () => {
            return ArrayHelper.initArrayWithValues<boolean>(this._symbolsNum, () => { return false; })
        });
        this.updateSymbolWinStates();

        // #315
        BarbarianFuryGameConfig.instance.WIN_BELOW_STAKE = (SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));
    }

    private updateSymbolWinStates():void {
        (<IBetLineWinData[]>this._gameData!.betWins!).forEach((betLineWinData:IBetLineWinData) => {
            for(let reelId:number = 0; reelId < betLineWinData.nrOfSymbols; reelId++) {
                const symbolId:number = betLineWinData.betLine[reelId];
                this._symbolWinStates[reelId][symbolId] = true;
            }
        });
    }

    /**
     * override this function to set noWin symbol state to disable.
     */
    protected playAllNoWinSymbols(timeline:Timeline):void {
        BFNudgeStackedWPController._symwinstates = this._symbolWinStates;
        this._symbolWinStates.forEach((reelWinStates:boolean[], reelId:number) => {
            reelWinStates.forEach((symbolState:boolean, symbolId:number) => {
                if(!symbolState) {
                    const noWinSymbol = Reels.getSymbol(reelId, symbolId);
                    if((this._nextMode === BarbarianFuryGameMode.FREESPIN || this._nextMode === BarbarianFuryGameMode.STACKED_RESPIN_FREESPIN) && noWinSymbol!.symName === "S") {
                        return;
                    }
                    if(noWinSymbol && !this._symbolWinStates[reelId][symbolId]) {
                        timeline.add(noWinSymbol.changeState({
                            state : BarbarianFuryGameConfig.instance.SYMBOL_STATES.noWin,
                            setStack : true,
                            fadeInDuration : 0
                        }), 0);
                    }
                }
            });
        });
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);
        //  added check to send aborted true if there is respin, and initial win is aborted => nudge before respin start
        if(this._gameData!.nextMode.indexOf("RESPIN") !== -1 && isAborted) {
            EventHandler.dispatchEvent(new GameEvent("SET_ABORTED_TRUE"));
        }
        EventHandler.dispatchEvent(new GameEvent("betLineEvent_hideAllBetLines"));
        this._winCountUp.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
        if(!this._gameData!.isBigWin) {
            SlotGame.winFieldController.showWinField();
            BarbarianFuryGameModuleConfig.foregroundAnimation.hideSymbolAnimation();
        }
    }

    protected addSymbolWinAnimationToTimeline(betLineWinData:IBetLineWinData, timeline:Timeline, startTime:number, symbol:BarbarianFuryReelSymbol, reelId:number, symbolId:number):number {
        BarbarianFuryGameModuleConfig.foregroundAnimation.playSymbolWinAnimation(reelId, symbolId);

        timeline.add(() => {
            // added this to add delay time while playing anim.
        }, reelId * 0.5);

        return timeline.duration();
    }
}